// Mécanique CSV propre aux exports Quantower.
//
// Quantower laisse l'utilisateur choisir le séparateur à l'export : virgule,
// point-virgule, tabulation ou espace. Les quatre variantes existent dans la
// nature (l'utilisateur en a fourni une de chaque). Les nombres suivent la
// locale : décimales à la VIRGULE (102,5). Les dates sont en JJ/MM/AAAA.
//
// Ces conversions sont volontairement isolées ici et n'ont pas vocation à être
// appliquées globalement : la virgule décimale corromprait un CSV natif où la
// virgule sépare les milliers.

const SEPARATORS = [",", ";", "\t", " "] as const;
export type Separator = (typeof SEPARATORS)[number];

/**
 * Retire le BOM UTF-8 en tête de fichier.
 *
 * Les exports Quantower en portent un. Sans strip, la première colonne se lit
 * "<BOM>Account" : la signature du dialecte ne matche pas et le fichier est
 * rejeté sans que rien n'indique pourquoi.
 */
export function stripBom(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

/**
 * Choisit le séparateur en comptant les occurrences HORS GUILLEMETS de la ligne
 * d'en-tête.
 *
 * Compter naïvement induirait deux erreurs :
 * - l'en-tête contient littéralement `Gross P/L,ticks`, une virgule dans un nom
 *   de colonne, qui ferait pencher un fichier point-virgule vers la virgule ;
 * - les noms composés (`Connection name`, `Symbol type`) contiennent des
 *   espaces, qui feraient passer un fichier virgule pour un fichier espace.
 *
 * En export espace ces noms sont entre guillemets ; les ignorer suffit à lever
 * les deux ambiguïtés.
 */
export function detectSeparator(headerLine: string): Separator {
  const counts = new Map<Separator, number>(SEPARATORS.map((s) => [s, 0]));
  let inQuotes = false;

  for (const char of headerLine) {
    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (inQuotes) continue;
    const sep = SEPARATORS.find((s) => s === char);
    if (sep) counts.set(sep, counts.get(sep)! + 1);
  }

  // Ordre de préférence : les séparateurs explicites priment sur l'espace,
  // qui reste le plus susceptible d'apparaître incidemment.
  let best: Separator = ",";
  let bestCount = -1;
  for (const sep of SEPARATORS) {
    const count = counts.get(sep)!;
    if (count > bestCount) {
      best = sep;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Découpe une ligne en cellules, en traitant le séparateur entre guillemets
 * comme du texte. Les guillemets sont retirés des cellules renvoyées.
 *
 * Cas particulier de l'espace : une colonne vide produit des séparateurs
 * CONSÉCUTIFS (`MNQU6  CME`). Les compter comme deux séparateurs décalerait
 * toutes les colonnes suivantes, donc on les fusionne. Pour la virgule, le
 * point-virgule et la tabulation, un champ vide est significatif et doit être
 * préservé.
 */
export function splitLine(line: string, separator: Separator): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  let quoted = false;

  for (const char of line) {
    if (char === '"') {
      inQuotes = !inQuotes;
      // Un champ explicitement guillemeté reste significatif même vide : "" est
      // une valeur, pas l'absence de valeur.
      quoted = true;
    } else if (char === separator && !inQuotes) {
      // Espace : deux séparateurs d'affilée viennent d'un champ vide non
      // guillemeté. On ne pousse pas de cellule, sinon les colonnes se
      // décalent... mais uniquement pour les runs d'espaces, jamais pour un
      // champ qui portait des guillemets.
      if (separator === " " && current === "" && !quoted) continue;
      cells.push(current.trim());
      current = "";
      quoted = false;
    } else {
      current += char;
    }
  }
  if (!(separator === " " && current === "" && !quoted)) {
    cells.push(current.trim());
  }

  return cells;
}

/**
 * Nombre à la française : `102,5` -> 102.5, `30.169,25` -> 30169.25.
 *
 * Les séparateurs de milliers doivent être retirés AVANT de convertir la
 * décimale. Sans ça, `30.169,25` se lirait `30.169` : un prix divisé par mille,
 * positif et fini, donc accepté sans broncher par la validation. Corruption
 * silencieuse sur des données financières — le pire cas.
 *
 * Renvoie 0 pour une valeur absente ou illisible, comme le `num()` des autres
 * dialectes — un frais manquant vaut zéro, pas NaN.
 */
export function frenchNumber(value: string | undefined): number {
  const raw = (value ?? "").trim();
  if (!raw) return 0;

  // La virgule est la décimale : tout point ou espace (y compris fine
  // insécable) qui la précède ne peut être qu'un séparateur de milliers.
  const normalized = raw.includes(",")
    ? raw.replace(/[.\s\u00a0\u202f]/g, "").replace(",", ".")
    : // Sans virgule, l'espace reste un séparateur de milliers, mais le point
      // est ambigu (30.169 peut être un décimal anglais). On ne touche qu'à
      // l'espace pour ne rien réinterpréter de travers.
      raw.replace(/[\s\u00a0\u202f]/g, "");

  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * `17/08/2026 16:20:57` -> `2026-08-17T16:20:57`.
 *
 * Le JOUR vient en premier. Passer la chaîne brute à `new Date()` la lirait à
 * l'américaine (mois/jour) et décalerait silencieusement les trades de
 * plusieurs mois. L'heure est optionnelle.
 *
 * Le format produit est sans fuseau : il sera interprété en heure locale, ce
 * qui correspond à l'heure que l'utilisateur voit dans Quantower.
 */
export function quantowerDate(value: string): string {
  const raw = (value ?? "").trim();
  if (!raw) return "";

  const [datePart, timePart] = raw.split(/\s+/);
  const match = (datePart ?? "").match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return "";

  const [, day, month, year] = match;
  const time = /^\d{2}:\d{2}:\d{2}$/.test(timePart ?? "") ? timePart : "00:00:00";
  return `${year}-${month}-${day}T${time}`;
}
